import type { TileDecodeContext, TileResourceData } from '@/plugins/tiles/contracts'
import type { GltfManifest } from '@/plugins/tiles/mesh/gltfManifest'
import { decodeDataUri } from '@/plugins/tiles/mesh/gltfManifest'
import { throwIfTilesAborted } from '@/plugins/tiles/TilesError'

/** 初次解码和图片质量更新共享 URL 去重、完整性及 buffer 长度校验。 */
export function createGltfDependencyReader(
  resource: TileResourceData,
  context: Pick<TileDecodeContext, 'signal' | 'read'>,
  maxBytes?: number
) {
  const reads = new Map<string, Promise<ArrayBuffer>>()
  let inputBytes = resource.data.byteLength
  const read = (uri: string): Promise<ArrayBuffer> => {
    const url = uri.startsWith('data:') ? uri : new URL(uri, resource.url).href
    let pending = reads.get(url)
    if (!pending) {
      pending = (async () => {
        throwIfTilesAborted(context.signal)
        const data = uri.startsWith('data:')
          ? decodeDataUri(uri)
          : await context.read(url, { signal: context.signal, maxBytes }).then((response) => {
              if (!response.complete) throw new Error('glTF 外部依赖响应不完整')
              return response.data
            })
        throwIfTilesAborted(context.signal)
        inputBytes += data.byteLength
        return data
      })()
      reads.set(url, pending)
    }
    return pending
  }
  return {
    read,
    get inputBytes() {
      return inputBytes
    },
    readBuffers: (manifest: GltfManifest) =>
      Promise.all(
        (manifest.json.buffers ?? []).map(
          async (buffer: any, index: number): Promise<Uint8Array<ArrayBuffer> | undefined> => {
            const data =
              buffer.uri !== undefined
                ? await read(buffer.uri)
                : index === 0
                  ? manifest.binary
                  : undefined
            // Meshopt 无物理 fallback 时保留缺省，不分配声明尺寸的占位数组。
            if (!data) return undefined
            if (data.byteLength < buffer.byteLength) throw new Error('glTF buffer 数据不足')
            return data instanceof Uint8Array
              ? data.subarray(0, buffer.byteLength)
              : new Uint8Array(data, 0, buffer.byteLength)
          }
        )
      ),
  }
}
